// CollapsedNavFlyout.tsx
// Hover-triggered flyout for a collapsed-sider parent nav item, listing its
// children. Bespoke — does not use AntD's SubMenu popup.
//
// Why bespoke: AntD's inline-collapsed SubMenu popup (`@rc-component/menu`'s
// PopupTrigger) delays its visible state through a `requestAnimationFrame`
// scheduled in a `useEffect` whose cleanup cancels it — the same
// effect-schedules-rAF / cleanup-cancels-it shape that gets permanently stuck
// under React 18 StrictMode's mount→unmount→remount double-invocation. This is
// the same category of bug documented on `FilterBar.tsx`'s custom popover
// (built for the same reason, for AntD's `<Popover>`). Agate wraps its app in
// `<StrictMode>`, so the AntD-native popup never becomes visible there. This
// component sidesteps the whole problem: no AntD motion, a portal + a plain
// CSS opacity transition (declarative, not effect/rAF driven).

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { theme } from 'antd';
import type { NavItem } from './types';

export interface CollapsedNavFlyoutProps {
  /** The parent NavItem — must have `children`. */
  item: NavItem;
  /** Already-resolved icon element for the trigger. */
  icon: React.ReactNode;
  /** Fired when a child is clicked. Wire to your router. */
  onSelect: (key: string, item: NavItem) => void;
}

/** Grace period before closing on mouse-leave, so moving diagonally from the
 *  trigger icon to the popover doesn't close it mid-transit. */
const CLOSE_GRACE_MS = 150;

export function CollapsedNavFlyout({ item, icon, onSelect }: CollapsedNavFlyoutProps) {
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // Gates visibility: hidden at the naive position until bottom-edge
  // collision correction (below) settles, so there's no one-frame flash of
  // an off-screen popover before it snaps into place.
  const [ready, setReady] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
    }
  };
  const handleEnter = () => {
    clearCloseTimer();
    setOpen(true);
  };
  const handleLeave = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), CLOSE_GRACE_MS);
  };

  useEffect(() => clearCloseTimer, []);
  useEffect(() => {
    if (!open) setReady(false);
  }, [open]);

  // Naive position: right of the trigger icon, top-aligned with it.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const computePos = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({ top: r.top, left: r.right + 8 });
    };
    computePos();
    window.addEventListener('scroll', computePos, true);
    window.addEventListener('resize', computePos);
    return () => {
      window.removeEventListener('scroll', computePos, true);
      window.removeEventListener('resize', computePos);
    };
  }, [open]);

  // Bottom-edge collision correction, same shape as FilterBar's right-edge
  // correction: measure after mount, flip if it overflows, guard against
  // re-firing once corrected.
  useLayoutEffect(() => {
    if (!open || !pos || !popoverRef.current) return;
    const VIEWPORT_MARGIN = 8;
    const pRect = popoverRef.current.getBoundingClientRect();
    if (pRect.bottom > window.innerHeight - VIEWPORT_MARGIN) {
      const newTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN - pRect.height);
      if (Math.abs(newTop - pos.top) > 0.5) {
        setPos({ top: newTop, left: pos.left });
        return;
      }
    }
    setReady(true);
  }, [open, pos]);

  const children = item.children ?? [];

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ display: 'inline-flex' }}
      >
        {icon}
      </span>
      {open && pos
        ? createPortal(
            <div
              ref={popoverRef}
              onMouseEnter={handleEnter}
              onMouseLeave={handleLeave}
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                zIndex: token.zIndexPopupBase,
                background: token.colorBgElevated,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                boxShadow: token.boxShadowSecondary,
                minWidth: 180,
                padding: `${token.paddingXS}px 0`,
                // AntD's sans `fontFamily` token only applies within its own
                // component tree (scoped to the `.br` class); this popover is
                // a bespoke portal to `document.body`, outside that scope,
                // so it falls back to the browser's serif default unless set
                // explicitly here.
                fontFamily: token.fontFamily,
                visibility: ready ? 'visible' : 'hidden',
                opacity: ready ? 1 : 0,
                transition: 'opacity 0.1s linear',
              }}
            >
              <div
                style={{
                  color: token.colorTextTertiary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '4px 16px 8px',
                }}
              >
                {item.label}
              </div>
              {children.map((child) => (
                <div
                  key={child.key}
                  role="menuitem"
                  // Disabled children stay listed (the flyout should read as
                  // the complete section) but are inert: out of the tab order,
                  // marked for assistive tech, and no click/key handlers.
                  aria-disabled={child.disabled || undefined}
                  tabIndex={child.disabled ? -1 : 0}
                  className="br-appshell-collapsed-flyout-item"
                  onClick={
                    child.disabled
                      ? undefined
                      : () => {
                          onSelect(child.key, child);
                          setOpen(false);
                        }
                  }
                  onKeyDown={
                    child.disabled
                      ? undefined
                      : (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelect(child.key, child);
                            setOpen(false);
                          }
                        }
                  }
                  style={{
                    padding: `${token.paddingXS}px ${token.padding}px`,
                    fontSize: token.fontSize,
                    color: child.disabled ? token.colorTextDisabled : token.colorText,
                    cursor: child.disabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  {child.label}
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
